/**
 * One credential field, rendered from what the manifest declared.
 *
 * Lives on its own because both ways of connecting need it — the compact
 * dialog and the full connection page — and a second copy would be the first
 * place the two drifted apart.
 */

import { useState } from 'react'
import type { CredentialField } from '@/core/types/api.types'

/** Credential fields that hold PEM text and therefore need a textarea. */
export const PEM_FIELDS = new Set([
  'private_key',
  'client_key',
  'client_cert',
  'ca_cert',
  'certificate',
])

export function CredentialInput({
  field,
  value,
  onChange,
}: {
  field: CredentialField
  value: unknown
  onChange: (value: unknown) => void
}) {
  const [reveal, setReveal] = useState(false)
  const isSecret = field.type === 'password'
  // PEM material is multi-line; a single-line input makes it unusable. Named
  // exactly rather than by suffix, because `api_key` is a suffix match and is
  // very much not a certificate.
  const multiline = PEM_FIELDS.has(field.key)

  return (
    <div className="field">
      <label className="field-label" htmlFor={`cred-${field.key}`}>
        {field.label}
        {field.required && <span className="field-req">*</span>}
      </label>

      {multiline ? (
        <textarea
          id={`cred-${field.key}`}
          className="textarea textarea-mono"
          rows={4}
          spellCheck={false}
          placeholder={field.placeholder ?? undefined}
          value={value === undefined || value === null ? '' : String(value)}
          onChange={(e) => onChange(e.target.value)}
        />
      ) : (
        <div className="row-tight" style={{ flexWrap: 'nowrap' }}>
          <input
            id={`cred-${field.key}`}
            className="input"
            type={isSecret && !reveal ? 'password' : field.type === 'number' ? 'number' : 'text'}
            autoComplete="off"
            spellCheck={false}
            placeholder={field.placeholder ?? undefined}
            value={value === undefined || value === null ? '' : String(value)}
            onChange={(e) => onChange(e.target.value)}
          />
          {isSecret && (
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => setReveal((v) => !v)}
              aria-label={reveal ? 'Hide' : 'Show'}
              tabIndex={-1}
            >
              {reveal ? '🙈' : '👁'}
            </button>
          )}
        </div>
      )}

      {field.help && <p className="field-help">{field.help}</p>}
    </div>
  )
}
